import type { Color } from "@material-ui/lab/Alert/Alert";
import type { DefaultTheme } from "styled-components";
import COLORS, { type IColorsInterface } from "styles/colors";

const MULTIPLIER = 10;
/** Base px for popover / dense panel corner math (e.g. audit quick filters). */
const COMPACT_CONTROL_PANEL_RADIUS_PX = 4;
const COMPACT_CONTROL_PANEL_BORDER_PX = 1.5;

export const MAIN_THEME: DefaultTheme = {
  multiplier: MULTIPLIER,
  avatar: `${3 * MULTIPLIER}px`,
  block: `${5 * MULTIPLIER}px`,
  /**
   * @deprecated use theme.border.color.light
   */
  lightBorder: `1px solid ${COLORS.brandGray10}`,
  darkBorder: "1px solid rgba(0,0,0,0.5)",
  lineHeight: `1.5`,
  borderRadius: "3px",

  colors: COLORS,

  spacing: {
    xsmall: `${0.5 * MULTIPLIER}px`,
    small: `${1 * MULTIPLIER}px`,
    medium: `${2 * MULTIPLIER}px`,
    large: `${3 * MULTIPLIER}px`,
    xlarge: `${3.5 * MULTIPLIER}px`,
  },

  breakpoint: {
    device: {
      xs: "320px",
      sm: "769px",
      lg: "1200px",
    },
  },

  border: {
    color: {
      light: COLORS.brandGray10,
    },
  },

  text: {
    color: {
      light: COLORS.brandWhite,
      medium: COLORS.brandGray30,
      dark: COLORS.brandGray50,
      tertiaryLight: "#6E6E73",
      disabledLight: "#A1A1A6",
    },

    primaryFamily: `"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
    mediumFamily: `"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,

    letterSpacing: {
      normal: "normal",
      title: "1px",
      smallCaps: "2.5px",
    },

    weight: {
      regular: 400,
      semiBold: 600,
      bold: 700,
    },

    size: {
      tiny: "10px",
      small: "12px",
      base: "14px",
      large: "16px",
      largest: "20px",
    },
  },

  shadows: {
    small: "5px 0 5px rgba(0,0,0,0.2)",
    medium: "0 0 10px rgba(0,0,0,0.2)",
    large: "0 0 20px rgba(0,0,0,0.2)",
  },

  layout: {
    sidebarWidth: "280px",
    toolbarHeight: `${6 * MULTIPLIER}px`,
    locationSwitcherWidth: "350px",
    contentHeading: {
      fontSize: "32px",
    },
  },

  layering: {
    base: 0,
    tooltip: 100,
    dropdown: 500,
    overlay: 900,
    modal: 1000,
    modalTooltip: 1100,
    modalDropdown: 1500,
  },

  dropdown: {
    shadow: "0px 2px 14px rgba(0, 0, 0, 0.2)",
  },

  components: {
    popover: {
      /** Outer corner radius for floating panels (e.g. popover paper). */
      panelRadiusOuter: `${COMPACT_CONTROL_PANEL_RADIUS_PX}px`,
      /** Full border width for hairline panel chrome, e.g. "1.5px". */
      panelBorderWidth: `${COMPACT_CONTROL_PANEL_BORDER_PX}px`,
      /** Inner radius: outer minus border (nested panel content). */
      panelContentRadius: `calc(${COMPACT_CONTROL_PANEL_RADIUS_PX}px - ${COMPACT_CONTROL_PANEL_BORDER_PX}px)`,
      /**
       * Vertical offset between popover trigger and paper (quick filter + time
       * range, audit log area).
       */
      offsetTop: "4px",
      /**
       * Elevation shadow for compact floating panels (audit popovers, aligned surfaces).
       */
      shadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
    },
    compactControl: {
      /** Row height for compact control surfaces (triggers, inline search). */
      rowHeight: "40px",
      /** Inline padding for dense list / menu rows. */
      listRowPaddingInline: "8px",
      /** Primary accent for borders / focus in compact controls. */
      accentBorder: "#1A73E8",
      /** Subtle fill for selected / highlighted compact controls. */
      accentSubtle: "#F2F9FF",
      /** Default hairline border color (popover chrome, closed trigger) vs accent. */
      neutralBorder: "#E0E0E0",
      /** Hover for text/controls on the accent border (e.g. links). */
      accentHover: "#1557B0",
      /**
       * Light wash on hover for bordered accent buttons (e.g. search commit) in
       * the compact audit area.
       */
      accentSurfaceHover: "rgba(26, 115, 232, 0.08)",
      /**
       * Focus ring for form controls in the compact accent system (2px, blue alpha).
       */
      focusRing: "0 0 0 2px rgba(26, 115, 232, 0.45)",
    },
  },

  iconSize: {
    xsmall: "18px",
    small: "25px",
    medium: "50px",
    large: "75px",
  },

  severity: {
    success: {
      backgroundLight: "rgba(69, 153, 31, 0.10)",
      light: "#45991F",
    },
    warning: {
      backgroundLight: "rgba(217, 178, 22, 0.10)",
      light: "#D9B216",
    },
    info: {
      backgroundLight: "rgba(77, 121, 234, 0.10)",
      light: "#D9B216",
    },
  },
};

interface ISpacingInterface {
  xsmall: string;
  small: string;
  medium: string;
  large: string;
  xlarge: string;
}

interface ITypeSizeInterface {
  tiny: string;
  small: string;
  base: string;
  large: string;
  largest: string;
}

interface ITypeLetterSpacingInterface {
  normal: string;
  smallCaps: string;
  title: string;
}

interface ITypeInterface {
  color: Record<
    "light" | "medium" | "dark" | "tertiaryLight" | "disabledLight",
    string
  >;
  primaryFamily: string;
  mediumFamily: string;
  letterSpacing: ITypeLetterSpacingInterface;
  weight: Record<"regular" | "semiBold" | "bold", string | number>;
  size: ITypeSizeInterface;
}

interface IBreakpointInterface {
  device: Record<"xs" | "sm" | "lg", string>;
}

interface IShadowInterface {
  small: string;
  medium: string;
  large: string;
}

interface ILayoutInterface {
  sidebarWidth: string;
  toolbarHeight: string;
  locationSwitcherWidth: string;
  contentHeading: {
    fontSize: string;
  };
}

interface ILayeringInterface {
  base: number;
  tooltip: number;
  dropdown: number;
  overlay: number;
  modal: number;
  modalTooltip: number;
  modalDropdown: number;
}

interface IDropdownInterface {
  shadow: string;
}

/** Shared UI tokens for compact controls and popover panels (audit / search and filter). */
export interface IThemeComponents {
  popover: {
    panelRadiusOuter: string;
    panelBorderWidth: string;
    panelContentRadius: string;
    offsetTop: string;
    shadow: string;
  };
  compactControl: {
    rowHeight: string;
    listRowPaddingInline: string;
    accentBorder: string;
    accentSubtle: string;
    neutralBorder: string;
    accentHover: string;
    accentSurfaceHover: string;
    focusRing: string;
  };
}

export interface IIconSizeInterface {
  xsmall: string;
  small: string;
  medium: string;
  large: string;
}

declare module "styled-components" {
  export interface DefaultTheme {
    multiplier: number;
    avatar: string;
    block: string;
    lightBorder: string;
    darkBorder: string;
    lineHeight: string;
    borderRadius: string;
    iconSize: IIconSizeInterface;
    colors: IColorsInterface;
    shadows: IShadowInterface;
    layout: ILayoutInterface;
    layering: ILayeringInterface;
    spacing: ISpacingInterface;
    text: ITypeInterface;
    border: {
      color: Record<"light", string>;
    };
    breakpoint: IBreakpointInterface;
    dropdown: IDropdownInterface;
    components: IThemeComponents;
    severity: Record<
      Color & ("warning" | "success" | "info"),
      Record<"backgroundLight" | "light", string>
    >;
  }
}
